import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Section, SectionHeading } from './Section';
import Reveal from './Reveal';
import Framed from './Framed';
import { SHOT_RATIO, leadShot } from './NizroShowcase';
import { nizroSignupUrl } from '../links';
import { routePath } from '../seo/routes';
import { emerald } from '../theme';
import wordmark from '../assets/img/nizro/nizro-wordmark-reversed.svg';

const CHECKS = ['nocard', 'ready', 'bilingual'];

// The home page keeps only a brief pointer to Nizro: one real screen, one
// summary, and two ways on. The trial goes straight to nizro.app, which is
// where an interested visitor should end up; the second button is for those
// who want the tour on /nizro first.
export default function NizroTeaser() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const shot = leadShot(lang);

  return (
    <Section id="nizro">
      <SectionHeading eyebrow={t('nizroTeaser.eyebrow')} title={t('nizroTeaser.title')} />
      <Reveal>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 4, md: 6 },
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
            alignItems: 'center',
          }}
        >
          <Framed
            src={shot.src}
            alt={t(`nizro.showcase.${shot.key}.alt`)}
            url="nizro.app"
            ratio={SHOT_RATIO}
            focus={shot.focus}
          />
          <Stack spacing={3} alignItems="flex-start">
            <Box component="img" src={wordmark} alt="Nizro" sx={{ display: 'block', height: 26 }} />
            <Typography sx={{ color: 'text.secondary', fontSize: 17 }}>{t('nizroTeaser.body')}</Typography>
            <Stack spacing={1}>
              {CHECKS.map((key) => (
                <Stack key={key} direction="row" spacing={1.25} alignItems="center">
                  <CheckRounded sx={{ fontSize: 18, color: emerald[400] }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(`nizro.promise.${key}.t`)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button
                size="large"
                variant="contained"
                href={nizroSignupUrl(lang)}
                target="_blank"
                rel="noopener"
                endIcon={<NorthEastRounded sx={{ '[dir="rtl"] &': { transform: 'scaleX(-1)' } }} />}
                sx={{ px: 3.5, py: 1.4, fontWeight: 800 }}
              >
                {t('nizro.ctaPrimary')}
              </Button>
              <Button
                component={RouterLink}
                to={routePath(lang, '/nizro')}
                size="large"
                variant="outlined"
                endIcon={<ArrowForwardRounded sx={{ '[dir="rtl"] &': { transform: 'scaleX(-1)' } }} />}
                sx={{ px: 3, py: 1.4, fontWeight: 700 }}
              >
                {t('nizroTeaser.cta')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Reveal>
    </Section>
  );
}
